'use client'

import { AppRail } from '@/components/app-rail'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { UserProvider } from '@/hooks/use-user-context'
import { ErrorBoundary } from '@/components/error-boundary'
import { PricingDialog } from '@/components/billing/pricing-dialog'
import { CreditStatusBanner } from '@/components/billing/credit-status-banner'
import {
  CHATGPT_ADS_BANNER_HEIGHT,
  ChatGptAdsAnnouncementBanner,
  useChatGptAdsAnnouncementVisible,
} from '@/components/chatgpt-ads-announcement-banner'
import { FeedbackWidget } from '@/components/feedback/feedback-widget'
import { SyncWidget } from '@/components/sync-widget/sync-widget'

// Desktop rail widths, set on the provider (the common ancestor of the gap +
// container elements — a value set on `<Sidebar>` itself would not reach the
// sibling gap element). Expanded is the labeled column; collapsed (Cmd+B or
// the header trigger) is the icon rail: labels fade out and rows gain
// right-side tooltips. 3rem is load-bearing: icons stay left-aligned at the
// same x in both states (8px container + 8px row padding puts a 16px icon's
// centre at 24px — exactly this rail's midline), so collapsing reads as one
// width slide with no icon re-centering snap. Mobile keeps the primitive's
// own 18rem off-canvas Sheet (it hardcodes its width separately).
const RAIL_WIDTH = '12.5rem'
const RAIL_WIDTH_ICON = '3rem'
const RAIL_SIDEBAR_STYLE = {
  '--sidebar-width': RAIL_WIDTH,
  '--sidebar-width-icon': RAIL_WIDTH_ICON,
} as React.CSSProperties

/**
 * Rail + main-content shell mounted by both `AppLayoutWrapper`
 * (non-brand authenticated routes) and `BrandLayout` (brand routes,
 * inside `CurrentBrandProvider`). Splitting it out lets the brand
 * layout wrap the rail in workspace context — without this,
 * `AppRail`'s `useOptionalCurrentBrand()` always returns
 * `null` because the provider sits below `{children}`.
 */
export function AppSidebarShell({
  children,
  defaultOpen = true,
}: {
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  return (
    <ErrorBoundary>
      <UserProvider>
        <AppSidebarShellInner defaultOpen={defaultOpen}>
          {children}
        </AppSidebarShellInner>
      </UserProvider>
    </ErrorBoundary>
  )
}

function AppSidebarShellInner({
  children,
  defaultOpen,
}: {
  children: React.ReactNode
  defaultOpen: boolean
}) {
  // The announcement strip mounts ABOVE the rail+content row so it spans the
  // full viewport width. The rail is `fixed inset-y-0`, so while the strip is
  // visible its top edge is pushed down via --app-banner-offset (consumed by
  // AppRail's Sidebar); everything reverts when the strip is dismissed.
  const bannerVisible = useChatGptAdsAnnouncementVisible()
  return (
    <div
      data-app-shell=""
      className="bg-app-shell-frame flex h-svh w-full flex-col"
      style={
        bannerVisible
          ? ({
              '--app-banner-offset': CHATGPT_ADS_BANNER_HEIGHT,
            } as React.CSSProperties)
          : undefined
      }
    >
      <ChatGptAdsAnnouncementBanner />
      <SidebarProvider
        defaultOpen={defaultOpen}
        style={RAIL_SIDEBAR_STYLE}
        className="min-h-0 flex-1"
      >
        <AppRail />
        {/* Inset the content as a rounded card floating on the dark frame, so
            the rail and the page read as one surface. Mobile keeps it
            edge-to-edge — the rail is an off-canvas Sheet there. */}
        <SidebarInset className="group/sidebar-inset min-h-0 overflow-hidden md:m-2 md:rounded-xl md:shadow-sm">
          <CreditStatusBanner />
          <SidebarTrigger className="bg-card fixed start-3 top-3 z-50 shadow-md group-has-[[data-workbench-overlay]]/sidebar-inset:invisible md:hidden" />
          <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
        </SidebarInset>
        <PricingDialog />
        <FeedbackWidget />
        <SyncWidget />
      </SidebarProvider>
    </div>
  )
}
